import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import bookingService from '../../services/bookingService';
import toast from 'react-hot-toast';

export const fetchBookings = createAsyncThunk(
  'booking/fetchBookings',
  async (params, { rejectWithValue }) => {
    try {
      const { data } = await bookingService.getBookings(params);
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to fetch bookings');
    }
  }
);

export const fetchBooking = createAsyncThunk(
  'booking/fetchBooking',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await bookingService.getBooking(id);
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to fetch booking');
    }
  }
);

export const createBooking = createAsyncThunk(
  'booking/createBooking',
  async (bookingData, { rejectWithValue }) => {
    try {
      const { data } = await bookingService.createBooking(bookingData);
      toast.success('Booking created successfully');
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to create booking');
    }
  }
);

export const approveBooking = createAsyncThunk(
  'booking/approveBooking',
  async ({ id, notes }, { rejectWithValue }) => {
    try {
      const { data } = await bookingService.approveBooking(id, notes);
      toast.success('Booking approved');
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to approve booking');
    }
  }
);

export const rejectBooking = createAsyncThunk(
  'booking/rejectBooking',
  async ({ id, reason }, { rejectWithValue }) => {
    try {
      const { data } = await bookingService.rejectBooking(id, reason);
      toast.success('Booking rejected');
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to reject booking');
    }
  }
);

export const cancelBooking = createAsyncThunk(
  'booking/cancelBooking',
  async ({ id, reason }, { rejectWithValue }) => {
    try {
      const { data } = await bookingService.cancelBooking(id, reason);
      toast.success('Booking cancelled');
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to cancel booking');
    }
  }
);

const initialState = {
  bookings: [],
  currentBooking: null,
  totalCount: 0,
  loading: false,
  detailLoading: false,
  actionLoading: false,
  error: null,
  stats: null,
};

const bookingSlice = createSlice({
  name: 'booking',
  initialState,
  reducers: {
    clearCurrentBooking: (state) => {
      state.currentBooking = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBookings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBookings.fulfilled, (state, action) => {
        state.loading = false;
        state.bookings = (() => { const _d = action.payload; return Array.isArray(_d?.results) ? _d.results : Array.isArray(_d?.items) ? _d.items : Array.isArray(_d) ? _d : []; })();
        state.totalCount = action.payload.count || 0;
      })
      .addCase(fetchBookings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchBooking.pending, (state) => {
        state.detailLoading = true;
      })
      .addCase(fetchBooking.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.currentBooking = action.payload;
      })
      .addCase(fetchBooking.rejected, (state, action) => {
        state.detailLoading = false;
        state.error = action.payload;
      })
      .addCase(createBooking.fulfilled, (state, action) => {
        state.bookings.unshift(action.payload);
      })
      .addCase(approveBooking.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(approveBooking.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.currentBooking = action.payload;
        const idx = state.bookings.findIndex((b) => b.id === action.payload.id);
        if (idx !== -1) state.bookings[idx] = action.payload;
      })
      .addCase(approveBooking.rejected, (state) => {
        state.actionLoading = false;
      })
      .addCase(rejectBooking.fulfilled, (state, action) => {
        state.currentBooking = action.payload;
        const idx = state.bookings.findIndex((b) => b.id === action.payload.id);
        if (idx !== -1) state.bookings[idx] = action.payload;
      })
      .addCase(cancelBooking.fulfilled, (state, action) => {
        state.currentBooking = action.payload;
        const idx = state.bookings.findIndex((b) => b.id === action.payload.id);
        if (idx !== -1) state.bookings[idx] = action.payload;
      });
  },
});

export const { clearCurrentBooking, clearError } = bookingSlice.actions;
export default bookingSlice.reducer;
